import { ReactNode } from "react";
import { ServiceTypeRow } from "../data/service-type-data";
import EmptyState from "../../../components/custom/empty-state";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";

const PAGE_SIZE_OPTIONS = [20, 50, 100];

interface ServiceTypeTableProps {
  rows: ServiceTypeRow[];
  onActivate?: (row: ServiceTypeRow) => void;
  onDeactivate?: (row: ServiceTypeRow) => void;
  onViewDetail?: (row: ServiceTypeRow) => void;
  onEdit?: (row: ServiceTypeRow) => void;
  isLoading?: boolean;
  startIndex?: number;
  page: number;
  pageSize: number;
  search: string;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSearch: (search: string) => void;
  totalRecords: number;
}

export const ServiceTypeTable: React.FC<ServiceTypeTableProps> = ({
  rows,
  onActivate,
  onDeactivate,
  onViewDetail,
  onEdit,
  isLoading = false,
  startIndex = 1,
  page,
  pageSize,
  search,
  setPage,
  setPageSize,
  setSearch,
  totalRecords,
}) => {
  const totalPages = Math.ceil(totalRecords / pageSize);
  // Khung bảng cao vừa tới đáy viewport, cuộn bên trong, tiêu đề cột đứng yên. `containerRef` bao cả thanh
  // phân trang để hook chừa chỗ cho nó; 40 = padding dưới của thẻ trắng (24) + padding của layout (16).
  const { ref: frameRef, containerRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>(40);

  return (
    <div ref={containerRef}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full max-w-xs items-center gap-2 md:justify-end">
          <input
            id="service-type-search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Tìm theo tên hoặc mã dịch vụ..."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            type="search"
          />
        </div>
      </div>


      <div ref={frameRef} style={{ maxHeight }} className="mt-6 overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full border-separate border-spacing-0 text-left text-sm [&_tr>*:last-child]:border-r-0 [&_tbody>tr:last-child>td]:border-b-0">
          <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-gray-100">
            <tr className="text-slate-600">
              <th scope="col" className="w-16 border-b border-r border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-center">
                STT
              </th>
              <th scope="col" className="border-b border-r border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Tên Loại dịch vụ
              </th>
              <th scope="col" className="border-b border-r border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Mã Loại dịch vụ
              </th>
              {/* Tạm ẩn: danh mục đang dùng chung cho mọi chi nhánh (branch_id = 0). Mở lại khi cần dịch vụ riêng theo chi nhánh
                  — nhớ tăng colSpan của dòng loading / rỗng lên 5.
              <th scope="col" className="border-b border-r border-gray-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide">
                Chi nhánh
              </th> */}
              <th scope="col" className="border-b border-r border-gray-200 px-4 py-3 text-xs text-xs font-semibold uppercase tracking-wide text-center">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="text-slate-700">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="border-b border-r border-gray-200 px-4 py-16 text-center text-sm font-medium text-slate-400">
                  <span className="inline-flex items-center justify-center gap-2 text-slate-500">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></span>
                    Đang tải dữ liệu...
                  </span>
                </td>
              </tr>
            ) : (
              <>
                {rows.map((row, index) => (
                  <tr key={`${row.id}-${index}`} className="hover:bg-slate-50">
                    <td className="border-b border-r border-gray-200 px-4 py-4 font-semibold text-slate-600 text-center">
                      {(page - 1) * pageSize + index + startIndex}
                    </td>
                    <td className="border-b border-r border-gray-200 px-4 py-4 capitalize">{row.name}</td>
                    <td className="border-b border-r border-gray-200 px-4 py-4">{row.code ?? row.serviceTypeId}</td>
                    {/* Tạm ẩn cột chi nhánh (danh mục dùng chung)
                    <td className="border-b border-r border-gray-200 px-4 py-4">{row.storeName}</td> */}
                    <td className="border-b border-r border-gray-200 px-4 py-4">
                      <div className="flex justify-start gap-2">
                        {onEdit ? (
                          <button
                            type="button"
                            onClick={() => onEdit(row)}
                            aria-label="Sửa"
                            className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-blue-500 text-white shadow-sm transition hover:bg-blue-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                              <path d="M21.731 2.269a2.625 2.625 0 0 0-3.714 0l-1.157 1.157 3.714 3.714 1.157-1.157a2.625 2.625 0 0 0 0-3.714Z" />
                              <path d="M19.513 8.199l-3.714-3.714-9.193 9.193a5.25 5.25 0 0 0-1.32 2.214l-.8 2.684a.75.75 0 0 0 .933.933l2.684-.8a5.25 5.25 0 0 0 2.214-1.32l9.193-9.193Z" />
                              <path d="M5.25 5.25h3a.75.75 0 0 1 0 1.5h-3A1.75 1.75 0 0 0 3.5 8.5v10A1.75 1.75 0 0 0 5.25 20.25h10a1.75 1.75 0 0 0 1.75-1.75v-3a.75.75 0 0 1 1.5 0v3A3.25 3.25 0 0 1 15.25 21.75h-10A3.25 3.25 0 0 1 2 18.5v-10A3.25 3.25 0 0 1 5.25 5.25Z" />
                            </svg>
                          </button>
                        ) : null}
                        {onDeactivate ? (
                          <button
                            type="button"
                            onClick={() => onDeactivate(row)}
                            aria-label="Tạm ngưng"
                            className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-white shadow-sm transition hover:bg-amber-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" className="h-4 w-4"><rect x="8" y="7" width="2.5" height="10" rx="0.75" stroke="currentColor" fill="currentColor" /><rect x="13.5" y="7" width="2.5" height="10" rx="0.75" stroke="currentColor" fill="currentColor" /></svg>
                          </button>
                        ) : null}
                        {onActivate ? (
                          <button type="button" onClick={() => onActivate(row)} aria-label="Kích hoạt" className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-emerald-500 text-white shadow-sm transition hover:bg-emerald-600">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M6 5.75v12.5a.75.75 0 0 0 1.125.65l10-6.25a.75.75 0 0 0 0-1.3l-10-6.25A.75.75 0 0 0 6 5.75Z" /></svg>
                          </button>
                        ) : null}
                        {onViewDetail && (
                          <button
                            type="button"
                            onClick={() => onViewDetail(row)}
                            className="grid h-9 w-9 place-items-center rounded-full bg-slate-500 text-white shadow-sm transition hover:bg-slate-600"
                            aria-label="Xem chi tiết"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="h-4 w-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="border-b border-r border-gray-200">
                      <EmptyState message="Chưa có dịch vụ nào" description="Không tìm thấy dịch vụ khớp bộ lọc." />
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <label className="flex items-center gap-3 text-sm text-slate-500">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            {PAGE_SIZE_OPTIONS.map((op) => (
              <option key={op} value={op}>
                {op}
              </option>
            ))}
          </select>
          <span>entries</span>
        </label>
        <div>
        </div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(page * pageSize, totalRecords)}</span> of{' '}
            <span className="font-medium">{totalRecords}</span> results
          </p>
        <div>
          <nav
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
            aria-label="Pagination"
          >
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span className="sr-only">Previous</span>
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                aria-current={p === page ? 'page' : undefined}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                  p === page
                    ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
            >
              <span className="sr-only">Next</span>
              &gt;
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default ServiceTypeTable;
